import {
  ENABLE_CREW_RANK_PERMISSIONS_UI,
  ENABLE_OPERATOR_SUB_ROLES_UI,
} from './rolePermissionsUiConfig'

/** Canonical Admin module READ / action permission codes (Registry 1.2.0 + backend-enforced codes). */
export const ADMIN_PERMS = {
  READ_COMPANY_GROUP_ADMIN: 'READ_COMPANY_GROUP_ADMIN',
  CREATE_COMPANY_GROUP_ADMIN: 'CREATE_COMPANY_GROUP_ADMIN',
  UPDATE_COMPANY_GROUP_ADMIN: 'UPDATE_COMPANY_GROUP_ADMIN',
  DELETE_COMPANY_GROUP_ADMIN: 'DELETE_COMPANY_GROUP_ADMIN',
  EXPORT_COMPANY_GROUP_ADMIN: 'EXPORT_COMPANY_GROUP_ADMIN',
  DOWNLOAD_COMPANY_GROUP_ADMIN: 'DOWNLOAD_COMPANY_GROUP_ADMIN',

  READ_COMPANY_ADMIN: 'READ_COMPANY_ADMIN',
  CREATE_COMPANY_ADMIN: 'CREATE_COMPANY_ADMIN',
  UPDATE_COMPANY_ADMIN: 'UPDATE_COMPANY_ADMIN',
  DELETE_COMPANY_ADMIN: 'DELETE_COMPANY_ADMIN',
  EXPORT_COMPANY_ADMIN: 'EXPORT_COMPANY_ADMIN',
  DOWNLOAD_COMPANY_ADMIN: 'DOWNLOAD_COMPANY_ADMIN',

  READ_VESSEL: 'READ_VESSEL',
  CREATE_VESSEL: 'CREATE_VESSEL',
  /** Enforced by {@code VesselService}; catalogue row seeded in RBAC v1.2.0 Admin pass. */
  UPDATE_VESSEL: 'UPDATE_VESSEL',
  DELETE_VESSEL: 'DELETE_VESSEL',
  EXPORT_VESSEL: 'EXPORT_VESSEL',
  DOWNLOAD_VESSEL: 'DOWNLOAD_VESSEL',

  READ_USER: 'READ_USER',
  CREATE_USER: 'CREATE_USER',
  /** Catalogue + legacy permissions table; user edit routes may also require role-specific UPDATE_* on backend. */
  UPDATE_USER: 'UPDATE_USER',
  /** Admin User Management password reset for another user (distinct from UPDATE_USER). */
  EDIT_USER_PASSWORD: 'EDIT_USER_PASSWORD',
  DELETE_USER: 'DELETE_USER',
  EXPORT_USER: 'EXPORT_USER',

  READ_ROLE: 'READ_ROLE',
  CREATE_ROLE: 'CREATE_ROLE',
  UPDATE_ROLE: 'UPDATE_ROLE',
  DELETE_ROLE: 'DELETE_ROLE',
  READ_PERMISSION: 'READ_PERMISSION',
  CREATE_PERMISSION: 'CREATE_PERMISSION',
  UPDATE_PERMISSION: 'UPDATE_PERMISSION',
  DELETE_PERMISSION: 'DELETE_PERMISSION',
  READ_ROLE_PERMISSION: 'READ_ROLE_PERMISSION',
  CREATE_ROLE_PERMISSION: 'CREATE_ROLE_PERMISSION',
  UPDATE_ROLE_PERMISSION: 'UPDATE_ROLE_PERMISSION',
  DELETE_ROLE_PERMISSION: 'DELETE_ROLE_PERMISSION',
  READ_CREW_RANK: 'READ_CREW_RANK',
  READ_MASTER_RANK: 'READ_MASTER_RANK',
  READ_CREW_RANK_PERMISSION: 'READ_CREW_RANK_PERMISSION',
  UPDATE_CREW_RANK_PERMISSION: 'UPDATE_CREW_RANK_PERMISSION',

  READ_VENDOR: 'READ_VENDOR',
  CREATE_VENDOR: 'CREATE_VENDOR',
  /** Backend vendor mutate endpoints use tenant scope today; UI gate aligns with production RBAC catalogue. */
  UPDATE_VENDOR: 'UPDATE_VENDOR',
  DELETE_VENDOR: 'DELETE_VENDOR',
  EXPORT_VENDOR: 'EXPORT_VENDOR',

  READ_REGION: 'READ_REGION',
  CREATE_REGION: 'CREATE_REGION',
  UPDATE_REGION: 'UPDATE_REGION',
  DELETE_REGION: 'DELETE_REGION',
} as const

export type AdminPermissionCode = typeof ADMIN_PERMS[keyof typeof ADMIN_PERMS]

export type PermissionReader = (code: string) => boolean

const isRolePermissionManagerBehavior = (
  effectiveBehaviorType: string | null | undefined,
  isSuperAdmin: boolean
): boolean =>
  isSuperAdmin || (effectiveBehaviorType || '').trim().toUpperCase() === 'COMPANY_GROUP_ADMIN'

export type AdminCrudGates = {
  canRead: boolean
  canCreate: boolean
  canUpdate: boolean
  canDelete: boolean
}

export const buildAdminCrudGates = (
  hasPermission: PermissionReader,
  isSuperAdmin: boolean,
  codes: {
    read: string
    create: string
    update: string
    delete: string
  }
): AdminCrudGates => {
  const allow = (code: string) => isSuperAdmin || hasPermission(code)
  return {
    canRead: allow(codes.read),
    canCreate: allow(codes.create),
    canUpdate: allow(codes.update),
    canDelete: allow(codes.delete),
  }
}

export const companyGroupAdminGates = (
  hasPermission: PermissionReader,
  isSuperAdmin: boolean
): AdminCrudGates =>
  buildAdminCrudGates(hasPermission, isSuperAdmin, {
    read: ADMIN_PERMS.READ_COMPANY_GROUP_ADMIN,
    create: ADMIN_PERMS.CREATE_COMPANY_GROUP_ADMIN,
    update: ADMIN_PERMS.UPDATE_COMPANY_GROUP_ADMIN,
    delete: ADMIN_PERMS.DELETE_COMPANY_GROUP_ADMIN,
  })

export const companyAdminGates = (
  hasPermission: PermissionReader,
  isSuperAdmin: boolean
): AdminCrudGates =>
  buildAdminCrudGates(hasPermission, isSuperAdmin, {
    read: ADMIN_PERMS.READ_COMPANY_ADMIN,
    create: ADMIN_PERMS.CREATE_COMPANY_ADMIN,
    update: ADMIN_PERMS.UPDATE_COMPANY_ADMIN,
    delete: ADMIN_PERMS.DELETE_COMPANY_ADMIN,
  })

export const vesselGates = (
  hasPermission: PermissionReader,
  isSuperAdmin: boolean
): AdminCrudGates =>
  buildAdminCrudGates(hasPermission, isSuperAdmin, {
    read: ADMIN_PERMS.READ_VESSEL,
    create: ADMIN_PERMS.CREATE_VESSEL,
    update: ADMIN_PERMS.UPDATE_VESSEL,
    delete: ADMIN_PERMS.DELETE_VESSEL,
  })

export const userManagementGates = (
  hasPermission: PermissionReader,
  hasAnyPermission: (codes: string[]) => boolean,
  isSuperAdmin: boolean,
  effectiveBehaviorType?: string | null
) => {
  const canResetPassword =
    isSuperAdmin ||
    hasPermission(ADMIN_PERMS.EDIT_USER_PASSWORD) ||
    hasPermission(ADMIN_PERMS.UPDATE_USER)
  const canManageRolePermissions = isRolePermissionManagerBehavior(
    effectiveBehaviorType,
    isSuperAdmin
  )
  return {
    canReadList: isSuperAdmin || hasPermission(ADMIN_PERMS.READ_USER),
    canCreateUser: isSuperAdmin || hasPermission(ADMIN_PERMS.CREATE_USER),
    canUpdateUser: isSuperAdmin || hasPermission(ADMIN_PERMS.UPDATE_USER),
    canResetPassword,
    canEditUserPassword: canResetPassword,
    canDeleteUser: isSuperAdmin || hasPermission(ADMIN_PERMS.DELETE_USER),
    canOpenRolePermissions:
      canManageRolePermissions &&
      hasAnyPermission([
        ADMIN_PERMS.READ_ROLE,
        ADMIN_PERMS.READ_ROLE_PERMISSION,
        ADMIN_PERMS.UPDATE_ROLE_PERMISSION,
      ]),
    canManageUserAccess:
      canManageRolePermissions &&
      (isSuperAdmin || hasPermission(ADMIN_PERMS.UPDATE_ROLE_PERMISSION)),
  }
}

export const vendorGates = (
  hasPermission: PermissionReader,
  isSuperAdmin: boolean
): AdminCrudGates =>
  buildAdminCrudGates(hasPermission, isSuperAdmin, {
    read: ADMIN_PERMS.READ_VENDOR,
    create: ADMIN_PERMS.CREATE_VENDOR,
    update: ADMIN_PERMS.UPDATE_VENDOR,
    delete: ADMIN_PERMS.DELETE_VENDOR,
  })

export const regionGates = (
  hasPermission: PermissionReader,
  isSuperAdmin: boolean
): AdminCrudGates =>
  buildAdminCrudGates(hasPermission, isSuperAdmin, {
    read: ADMIN_PERMS.READ_REGION,
    create: ADMIN_PERMS.CREATE_REGION,
    update: ADMIN_PERMS.UPDATE_REGION,
    delete: ADMIN_PERMS.DELETE_REGION,
  })

export const canOpenAdminAccessManagementByPermission = (
  hasAnyPermission: (codes: string[]) => boolean,
  isSuperAdmin: boolean,
  effectiveBehaviorType?: string | null
): boolean =>
  isRolePermissionManagerBehavior(effectiveBehaviorType, isSuperAdmin) &&
  hasAnyPermission([
    ADMIN_PERMS.READ_ROLE,
    ADMIN_PERMS.READ_ROLE_PERMISSION,
    ADMIN_PERMS.UPDATE_ROLE_PERMISSION,
    ADMIN_PERMS.READ_PERMISSION,
  ])

export type RolePermissionManagementGates = {
  canReadRole: boolean
  canCreateRole: boolean
  canUpdateRole: boolean
  canDeleteRole: boolean
  canReadPermission: boolean
  canReadRolePermission: boolean
  canSaveRolePermissions: boolean
  canEditCrewRankPermissions: boolean
  showRoleManagementTab: boolean
  showRolesTab: boolean
  showOperatorSubRolesTab: boolean
  showCrewRankPermissionsTab: boolean
}

export const rolePermissionManagementGates = (
  hasPermission: PermissionReader,
  hasAnyPermission: (codes: string[]) => boolean,
  isSuperAdmin: boolean,
  effectiveBehaviorType?: string | null
): RolePermissionManagementGates => {
  const allow = (code: string) => isSuperAdmin || hasPermission(code)
  const behavior = (effectiveBehaviorType || '').trim().toUpperCase()
  const isOperator = behavior === 'OPERATOR'
  const canManageRolePermissions = isRolePermissionManagerBehavior(behavior, isSuperAdmin)
  const canReadPermission = allow(ADMIN_PERMS.READ_PERMISSION)
  const canReadRolePermission = allow(ADMIN_PERMS.READ_ROLE_PERMISSION)
  const canReadCrewRankPermissions = allow(ADMIN_PERMS.READ_CREW_RANK_PERMISSION)
  const canUpdateCrewRankPermissions = allow(ADMIN_PERMS.UPDATE_CREW_RANK_PERMISSION)

  return {
    canReadRole: allow(ADMIN_PERMS.READ_ROLE),
    canCreateRole: allow(ADMIN_PERMS.CREATE_ROLE),
    canUpdateRole: allow(ADMIN_PERMS.UPDATE_ROLE),
    canDeleteRole: allow(ADMIN_PERMS.DELETE_ROLE),
    canReadPermission,
    canReadRolePermission,
    canSaveRolePermissions:
      canManageRolePermissions &&
      hasAnyPermission([ADMIN_PERMS.CREATE_ROLE_PERMISSION, ADMIN_PERMS.UPDATE_ROLE_PERMISSION]),
    canEditCrewRankPermissions:
      isSuperAdmin || (canReadCrewRankPermissions && canUpdateCrewRankPermissions),
    showRoleManagementTab: canManageRolePermissions && allow(ADMIN_PERMS.READ_ROLE),
    showRolesTab: canManageRolePermissions && canReadPermission && canReadRolePermission,
    showOperatorSubRolesTab:
      ENABLE_OPERATOR_SUB_ROLES_UI && (isSuperAdmin || (isOperator && canReadRolePermission)),
    showCrewRankPermissionsTab:
      ENABLE_CREW_RANK_PERMISSIONS_UI && (isSuperAdmin || canReadCrewRankPermissions),
  }
}
